/**
 * [TD-47.3] DraftQueue - full queue manager rendered as the 4th right-pane
 * tab (Gap 1 verdict D - Hybrid placement). Locked behaviors:
 *
 *   • Drag-to-reorder via HTML5 native (Gap 5 verdict A) - no new dep.
 *   • Chevron Up/Down a11y fallback for keyboard + touch users.
 *   • Empty-state copy is spec-locked: "Use your time between picks to
 *     build a queue." (§3.2)
 *   • Clear-all requires confirm (two-step, mirrors action drawer pattern).
 *   • Click a queued row → publishes `setSelectedPlayerId` via
 *     DraftFiltersContext; SnakePlayerPool reads it and opens the drawer.
 *     This is the read-side of the Top-of-Queue → drawer hand-off.
 *
 * [Item 2] The queue is the manager's watchlist AND their draft surface:
 *   • Auto-Draft toggle sits in this panel's header (snake only) and is
 *     ALWAYS visible so it can be armed before a queue exists. The
 *     auto-submit engine itself lives in AutoDraftContext, one level up -
 *     TabbedPanel unmounts inactive tabs, so orchestration in here would
 *     die the moment the manager switched tabs.
 *   • Each row carries a Draft pill, live only on the manager's turn.
 *   • Drafted players are pruned from the queue by `useDraftQueue`, so
 *     there is no "taken" row state to render any more.
 */
import { useState } from "react";
import { GripVertical, ChevronUp, ChevronDown, Trash2, Info } from "lucide-react";
import PositionBadge from "../PositionBadge";
import { useDraftQueueContext } from "../../contexts/DraftQueueContext";
import { useDraftFilters } from "../../contexts/DraftFiltersContext";
import { useAutoDraft } from "../../contexts/AutoDraftContext";

const AutoDraftToggle = ({ enabled, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    onClick={() => onChange(!enabled)}
    data-testid="snake-auto-draft-queue-toggle"
    className="flex items-center gap-1.5 px-1.5 py-1 rounded-md hover:bg-[#FF6B00]/10 transition-colors"
  >
    <span
      className="font-display font-black text-[9px] uppercase tracking-widest"
      style={{ color: enabled ? "#00C853" : "rgba(255,255,255,0.50)" }}
    >
      Auto-Draft
    </span>
    <span
      className="relative inline-flex items-center shrink-0"
      style={{
        width: 26,
        height: 15,
        background: enabled ? "#00C853" : "#3A3A3A",
        borderRadius: 9999,
        transition: "background-color 180ms ease",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: enabled ? 13 : 2,
          width: 11,
          height: 11,
          background: "#FFFFFF",
          borderRadius: 9999,
          transition: "left 180ms ease",
          boxShadow: "0 1px 2px rgba(0,0,0,0.35)",
        }}
      />
    </span>
  </button>
);

export default function DraftQueue() {
  const {
    queue,
    removeFromQueue,
    reorderQueue,
    moveUp,
    moveDown,
    clearQueue,
    takenIds,
  } = useDraftQueueContext();
  const { setSelectedPlayerId } = useDraftFilters();
  const {
    available: isSnake, enabled: autoDraftOn, setEnabled: setAutoDraftOn,
    isMyTurn, draftNow,
  } = useAutoDraft();

  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [draftingId, setDraftingId] = useState(null);
  const [showAutoDraftInfo, setShowAutoDraftInfo] = useState(false);

  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(idx));
    } catch {
      /* some browsers throw on programmatic setData */
    }
  };

  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };

  const onDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== idx) {
      reorderQueue(dragIdx, idx);
    }
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const onDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const handleRowClick = (player) => {
    setSelectedPlayerId?.(player.id);
  };

  const handleClearClick = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 2500);
      return;
    }
    clearQueue();
    setConfirmClear(false);
  };

  // One tap = the pick. No pool hunt, no confirm drawer - the manager
  // already committed by queueing them in this order.
  const handleDraftClick = async (player) => {
    if (!isMyTurn || draftingId) return;
    setDraftingId(player.id);
    try {
      await draftNow(player.id);
    } finally {
      setDraftingId(null);
    }
  };

  return (
    <div
      className="flex flex-col h-full min-h-0 bg-[#1C1C1C] border border-[#252525] rounded-md overflow-hidden"
      data-testid="draft-queue"
    >
      {/* Header */}
      <div className="px-3 py-2 border-b border-[#252525] shrink-0 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-display font-black text-[10px] uppercase tracking-widest text-[#FF6B00]">
            Your Queue
          </span>
          <span
            className="text-[10px] font-mono tabular-nums text-[rgba(255,255,255,0.45)]"
            data-testid="draft-queue-count"
          >
            {queue.length}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isSnake && (
            <>
              <AutoDraftToggle enabled={autoDraftOn} onChange={setAutoDraftOn} />
              <button
                type="button"
                onClick={() => setShowAutoDraftInfo((v) => !v)}
                data-testid="draft-queue-autodraft-info-btn"
                aria-label="How autodraft works"
                aria-expanded={showAutoDraftInfo}
                className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                  showAutoDraftInfo
                    ? "text-[#FF6B00]"
                    : "text-[rgba(255,255,255,0.45)] hover:text-[#FF6B00]"
                }`}
              >
                <Info size={11} />
              </button>
            </>
          )}
          {queue.length > 0 && (
            <button
              type="button"
              onClick={handleClearClick}
              data-testid="draft-queue-clear-btn"
              data-confirm={confirmClear ? "true" : undefined}
              className={`flex items-center gap-1 text-[9px] font-display font-black uppercase tracking-wider px-2 py-1 rounded border transition-all ${
                confirmClear
                  ? "bg-[#FF4444]/15 border-[#FF4444]/50 text-[#FF4444]"
                  : "bg-[#252525] border-[#2E2E2E] text-[rgba(255,255,255,0.50)] hover:border-[#FF4444]/40 hover:text-[#FF4444]"
              }`}
            >
              <Trash2 size={9} />
              {confirmClear ? "Confirm" : "Clear"}
            </button>
          )}
        </div>
      </div>

      {/* [Autodraft hint] Tappable, not hover-only: the toggle is used on
          phones too, where a tooltip is unreachable. Same inline banner
          pattern as the empty-queue warning below. */}
      {isSnake && showAutoDraftInfo && (
        <p
          className="px-3 py-2 shrink-0 font-body text-[10px] leading-snug text-[rgba(255,255,255,0.70)] bg-[#252525] border-b border-[#2E2E2E]"
          data-testid="draft-queue-autodraft-info"
        >
          Autodraft runs in your browser — keep this tab open. If you close it,
          your queue won't pick for you.
        </p>
      )}

      {/* [Item 2] Armed with nothing to fire: the server clock still covers
          the manager, but say so rather than letting them assume the queue
          is doing the work. */}
      {isSnake && autoDraftOn && queue.length === 0 && (
        <p
          className="px-3 py-2 shrink-0 font-body text-[10px] leading-snug text-[#FF6B00] bg-[#FF6B00]/10 border-b border-[#FF6B00]/25"
          data-testid="draft-queue-autodraft-empty-warning"
        >
          Autodraft is ON but your queue is empty — the clock will auto-pick best
          available.
        </p>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {queue.length === 0 ? (
          <div
            className="h-full flex flex-col items-center justify-center px-4 text-center"
            data-testid="draft-queue-empty"
          >
            <p className="font-body text-xs text-[rgba(255,255,255,0.45)] leading-snug">
              Use your time between picks to build a queue.
            </p>
            <p className="font-body text-[10px] text-[rgba(255,255,255,0.30)] mt-2">
              Tap the <span className="text-[#FF6B00]">+</span> next to a
              player in the pool to add them here.
            </p>
          </div>
        ) : (
          queue.map((player, idx) => {
            const isFirst = idx === 0;
            const isLast = idx === queue.length - 1;
            const isDragOver = dragOverIdx === idx && dragIdx !== idx;
            // Pruning is authoritative, but a pick can land between renders.
            const stillAvailable = !takenIds?.has?.(player.id);
            const canDraft = isSnake && isMyTurn && stillAvailable;
            return (
              <div
                key={player.id}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver(idx)}
                onDrop={onDrop(idx)}
                onDragEnd={onDragEnd}
                onClick={() => handleRowClick(player)}
                data-testid={`draft-queue-row-${player.id}`}
                data-idx={idx}
                className={`flex items-center gap-1.5 px-2 py-1.5 border-b border-[#252525]/50 transition-colors cursor-pointer hover:bg-[#FF6B00]/10 ${
                  isDragOver ? "bg-[#FF6B00]/15" : ""
                } ${dragIdx === idx ? "opacity-40" : ""} ${
                  isFirst ? "bg-[#00C853]/5" : ""
                }`}
              >
                <span
                  className="text-[rgba(255,255,255,0.30)] cursor-grab"
                  aria-hidden="true"
                  data-testid={`draft-queue-drag-${player.id}`}
                >
                  <GripVertical size={11} />
                </span>
                <span
                  className="font-mono text-[9px] tabular-nums w-4 shrink-0 text-[rgba(255,255,255,0.45)]"
                  data-testid={`draft-queue-rank-${player.id}`}
                >
                  {idx + 1}
                </span>
                <PositionBadge position={player.position} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="font-body text-xs text-[#F5F5F5] truncate font-medium">
                    {player.name}
                  </p>
                  <p className="font-body text-[10px] text-[rgba(255,255,255,0.40)] truncate">
                    {player.nfl_team} · Bye {player.bye_week}
                  </p>
                </div>
                {isSnake && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDraftClick(player);
                    }}
                    disabled={!canDraft || draftingId === player.id}
                    aria-label={`Draft ${player.name}`}
                    data-testid={`draft-queue-draft-${player.id}`}
                    className={`shrink-0 rounded-full px-2 py-[3px] text-[9px] font-display font-black uppercase tracking-wider border transition-all ${
                      canDraft
                        ? "bg-[#FF6B00] border-[#FF6B00] text-[#0A0A0A] hover:brightness-110"
                        : "bg-transparent border-[#2E2E2E] text-[rgba(255,255,255,0.25)] cursor-not-allowed"
                    } disabled:opacity-60`}
                  >
                    {draftingId === player.id ? "..." : "Draft"}
                  </button>
                )}
                {/* Chevron a11y fallback */}
                <div className="flex flex-col gap-0 shrink-0">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveUp(player.id);
                    }}
                    disabled={isFirst}
                    aria-label={`Move ${player.name} up`}
                    data-testid={`draft-queue-up-${player.id}`}
                    className="text-[rgba(255,255,255,0.40)] hover:text-[#FF6B00] disabled:opacity-20 disabled:cursor-not-allowed leading-none"
                  >
                    <ChevronUp size={11} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveDown(player.id);
                    }}
                    disabled={isLast}
                    aria-label={`Move ${player.name} down`}
                    data-testid={`draft-queue-down-${player.id}`}
                    className="text-[rgba(255,255,255,0.40)] hover:text-[#FF6B00] disabled:opacity-20 disabled:cursor-not-allowed leading-none"
                  >
                    <ChevronDown size={11} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFromQueue(player.id);
                  }}
                  aria-label={`Remove ${player.name} from queue`}
                  data-testid={`draft-queue-remove-${player.id}`}
                  className="shrink-0 text-[rgba(255,255,255,0.30)] hover:text-[#FF4444] transition-colors"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
